import { isApiError } from "api/ApiError";

// Primitives shared by the client-side polling loops (agent jobs, USDT
// deposits). A cancelled request rejects with an aborted ApiError, a sleep
// cut short with the signal's AbortError; both read as aborted here.

export const isAbortError = (error: unknown): boolean =>
  (isApiError(error) && error.kind === "aborted") ||
  (typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "AbortError");

export const abortReason = (signal: AbortSignal) =>
  signal.reason ?? new DOMException("The operation was aborted.", "AbortError");

// status is null when the request never reached the server. Anything else
// (404, 422, our own bugs) is not worth retrying, and neither is an abort or
// an error thrown before the request went out.
export const isTransientError = (error: unknown) => {
  if (isAbortError(error)) return false;
  if (isApiError(error) && error.kind === "unknown") return false;
  const status = (error as { status?: unknown } | null)?.status;
  return (
    status === null ||
    status === 429 ||
    (typeof status === "number" && status >= 500)
  );
};

export const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortReason(signal));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortReason(signal!));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
